import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePets } from '../contexts/PetContext';
import axios from 'axios';
import {
  Send,
  MessageCircle,
  Brain,
  Loader2,
  Heart,
  User,
  Bot,
  AlertCircle,
  Plus,
  Zap,
  Sparkles,
  Image as ImageIcon,
  X,
  FileText,
  Stethoscope,
  ShieldPlus,
  Syringe,
  Clock3
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const CHAT_REQUEST_TIMEOUT_MS = 150000;

const Chat = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { pets, loading: petsLoading, fetchPets } = usePets();
  const [selectedPet, setSelectedPet] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesByPet, setMessagesByPet] = useState({});
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [sessionByPet, setSessionByPet] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImagePreview, setSelectedImagePreview] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [assessmentByPet, setAssessmentByPet] = useState({});
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);
  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);

  useEffect(() => {
    if (user && Array.isArray(pets) && pets.length === 0 && !petsLoading) {
      fetchPets();
    }
  }, [user, pets, petsLoading, fetchPets]);

  useEffect(() => {
    setSessionId(`session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const quickActions = useMemo(() => [
    { text: t('chat_quick_1'), icon: Heart },
    { text: t('chat_quick_2'), icon: ShieldPlus },
    { text: t('chat_quick_3'), icon: Syringe },
    { text: t('chat_quick_4'), icon: Clock3 }
  ], [t]);

  const formatSpecies = (species) => {
    if (!species) return '';
    const key = `pets_species_${String(species).toLowerCase()}`;
    const translated = t(key);
    return translated === key ? species : translated;
  };

  const uploadMedicalDocument = useCallback(async () => {
    if (!selectedPet?.id) {
      setError(t('chat_select_pet_before_upload'));
      return;
    }
    if (!selectedDocument) return;

    setIsUploadingDoc(true);
    setError('');

    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      formData.append('document', selectedDocument);
      formData.append('document_type', 'other');
      formData.append('notes', 'Uploaded from chat page');

      const res = await axios.post(`/pets/${selectedPet.id}/upload_medical_document/`, formData, {
        timeout: CHAT_REQUEST_TIMEOUT_MS,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadedDocs((prev) => [res.data, ...prev].slice(0, 5));
      setSelectedDocument(null);
      if (docInputRef.current) docInputRef.current.value = '';
    } catch (uploadError) {
      console.error('Medical document upload failed:', uploadError);
      setError(t('chat_upload_document_failed'));
    } finally {
      setIsUploadingDoc(false);
    }
  }, [selectedPet, selectedDocument, t]);

  const pollTaskUntilDone = useCallback(async (taskId, token) => {
    const maxAttempts = 60;
    for (let i = 0; i < maxAttempts; i += 1) {
      const res = await axios.get(`/assessments/task_status/?task_id=${encodeURIComponent(taskId)}`, {
        timeout: 30000,
        headers: { Authorization: `Bearer ${token}` }
      });
      const state = res.data?.state;
      if (state === 'SUCCESS') return res.data?.result;
      if (state === 'FAILURE') throw new Error(res.data?.error || t('chat_async_failed'));
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }
    throw new Error(t('chat_async_timeout'));
  }, [t]);

  const renderFormattedContent = useCallback((text) => {
    if (!text) return null;
    const lines = text.split('\n');
    const blocks = [];
    let currentList = [];

    const flushList = () => {
      if (currentList.length > 0) {
        blocks.push(
          <ul className="list-disc space-y-1 pl-5" key={`ul-${blocks.length}`}>
            {currentList.map((item, idx) => (
              <li key={`li-${blocks.length}-${idx}`} dangerouslySetInnerHTML={{ __html: item }} />
            ))}
          </ul>
        );
        currentList = [];
      }
    };

    const boldify = (value) => value.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    lines.forEach((raw) => {
      const line = raw.trim();
      if (line.startsWith('- ')) {
        currentList.push(boldify(line.replace(/^-\s/, '')));
      } else if (!line) {
        flushList();
        blocks.push(<div className="h-2" key={`sp-${blocks.length}`} />);
      } else {
        flushList();
        blocks.push(
          <p className="text-sm" key={`p-${blocks.length}`} dangerouslySetInnerHTML={{ __html: boldify(line) }} />
        );
      }
    });

    flushList();
    return <div className="space-y-1">{blocks}</div>;
  }, []);

  const ensureAssessmentForPet = useCallback(async (petId, token) => {
    if (assessmentByPet[petId]) return assessmentByPet[petId];
    const createRes = await axios.post('/assessments/create_assessment/', { pet_id: petId }, {
      timeout: 30000,
      headers: { Authorization: `Bearer ${token}` }
    });
    const newId = createRes.data.id;
    setAssessmentByPet((prev) => ({ ...prev, [petId]: newId }));
    return newId;
  }, [assessmentByPet]);

  const sendMessage = useCallback(async () => {
    if (!newMessage.trim() || isLoading) return;

    if (abortControllerRef.current) abortControllerRef.current.abort();

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: newMessage,
      isImageConsult: Boolean(selectedImage),
      imageCount: selectedImage ? 1 : 0,
      imageNames: selectedImage ? [selectedImage.name] : [],
      timestamp: new Date().toISOString()
    };

    setMessages((prev) => {
      const next = [...prev, userMessage];
      if (selectedPet?.id) {
        setMessagesByPet((map) => ({ ...map, [selectedPet.id]: next }));
      }
      return next;
    });

    const messageText = newMessage;
    setNewMessage('');
    setIsLoading(true);
    setIsTyping(true);
    setError('');
    setConnectionStatus('connecting');
    abortControllerRef.current = new AbortController();

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError(t('chat_auth_required'));
        return;
      }

      let response;

      if (selectedImage) {
        if (!selectedPet?.id) {
          setError(t('chat_select_pet_photo'));
          throw new Error('pet-required-for-image');
        }

        const assessmentId = await ensureAssessmentForPet(selectedPet.id, token);
        const formData = new FormData();
        formData.append('message', messageText);
        formData.append('image', selectedImage);
        formData.append('async', 'true');

        response = await axios.post(`/assessments/${assessmentId}/consult_with_image/`, formData, {
          signal: abortControllerRef.current.signal,
          timeout: CHAT_REQUEST_TIMEOUT_MS,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.status === 202 && response.data?.task_id) {
          const asyncResult = await pollTaskUntilDone(response.data.task_id, token);
          response = {
            data: {
              bot_response: {
                content: asyncResult?.bot_response?.message,
                intent: asyncResult?.bot_response?.intent
              }
            }
          };
        }
      } else {
        response = await axios.post('/chat/', {
          message: messageText,
          pet_id: selectedPet?.id,
          session_id: sessionId
        }, {
          signal: abortControllerRef.current.signal,
          timeout: CHAT_REQUEST_TIMEOUT_MS,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        });
      }

      setConnectionStatus('connected');
      setIsTyping(false);

      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: response.data?.bot_response?.content || response.data.message || response.data.response || t('chat_default_reply'),
        intent: response.data?.bot_response?.intent || response.data.intent,
        sentiment: response.data.sentiment,
        timestamp: response.data.timestamp || new Date().toISOString()
      };

      setMessages((prev) => {
        const next = [...prev, aiMessage];
        if (selectedPet?.id) {
          setMessagesByPet((map) => ({ ...map, [selectedPet.id]: next }));
        }
        return next;
      });

      setSelectedImage(null);
      setSelectedImagePreview('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (sendError) {
      if (sendError.message !== 'pet-required-for-image') {
        console.error('Chat error:', sendError);
      }

      if (sendError.name === 'AbortError') {
        setError(t('chat_request_cancelled'));
        return;
      }

      let errorMessage = t('assess_chat_error');

      if (sendError.code === 'ECONNABORTED' || String(sendError.message || '').includes('timeout')) {
        setError(t('chat_timeout_error'));
        errorMessage = t('chat_timeout_reply');
      } else if (sendError.response?.status === 401) {
        setError(t('chat_auth_error'));
        errorMessage = t('chat_auth_reply');
      } else if (sendError.response?.status >= 500) {
        setError(t('chat_server_error'));
        errorMessage = t('chat_server_reply');
      } else if (sendError.message !== 'pet-required-for-image') {
        setError(t('chat_send_failed'));
      }

      setConnectionStatus('error');

      if (sendError.message !== 'pet-required-for-image') {
        setMessages((prev) => {
          const next = [...prev, {
            id: Date.now() + 1,
            type: 'ai',
            content: errorMessage,
            timestamp: new Date().toISOString()
          }];
          if (selectedPet?.id) {
            setMessagesByPet((map) => ({ ...map, [selectedPet.id]: next }));
          }
          return next;
        });
      }
    } finally {
      setIsLoading(false);
      setIsTyping(false);
      abortControllerRef.current = null;
    }
  }, [newMessage, isLoading, selectedImage, selectedPet, sessionId, ensureAssessmentForPet, pollTaskUntilDone, t]);

  const handleKeyPress = useCallback((event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  const handleQuickAction = useCallback((actionText) => {
    setNewMessage(actionText);
  }, []);

  const switchPetConversation = useCallback((pet) => {
    setSelectedPet(pet);
    setMessages(messagesByPet[pet.id] || []);
    let petSession = sessionByPet[pet.id];
    if (!petSession) {
      petSession = `session_${pet.id}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      setSessionByPet((prev) => ({ ...prev, [pet.id]: petSession }));
    }
    setSessionId(petSession);
  }, [messagesByPet, sessionByPet]);

  const runPetPrivacyAction = useCallback(async (actionPath, successText) => {
    if (!selectedPet?.id) {
      setError(t('chat_select_pet_first'));
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`/pets/${selectedPet.id}/${actionPath}/`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (actionPath === 'clear_chat_history') {
        setMessages([]);
        setMessagesByPet((prev) => ({ ...prev, [selectedPet.id]: [] }));
        setAssessmentByPet((prev) => ({ ...prev, [selectedPet.id]: undefined }));
      }
      setError(successText);
    } catch (privacyError) {
      console.error('Privacy action failed:', privacyError);
      setError(t('chat_action_failed'));
    }
  }, [selectedPet, t]);

  const startNewChat = () => {
    if (selectedPet?.id) {
      setMessagesByPet((prev) => ({ ...prev, [selectedPet.id]: [] }));
    }
    setMessages([]);
    setError('');
    setSelectedImage(null);
    setSelectedImagePreview('');
    setSessionId(`session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`);
  };

  if (petsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-blue-600" />
          <p className="text-gray-600">{t('loading_pets')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b border-slate-200/70 bg-white/80 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center space-x-3">
                <div className="rounded-lg bg-blue-100 p-2">
                  <MessageCircle className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">{t('chat_title')}</h2>
                  <div className="mt-1 flex items-center space-x-2">
                    <p className="text-sm text-gray-500">{t('chat_subtitle')}</p>
                    <div className="flex items-center space-x-1">
                      {connectionStatus === 'connected' && <div className="h-2 w-2 animate-pulse rounded-full bg-green-500" />}
                      {connectionStatus === 'connecting' && <div className="h-2 w-2 animate-pulse rounded-full bg-yellow-500" />}
                      {connectionStatus === 'error' && <div className="h-2 w-2 rounded-full bg-red-500" />}
                      {isTyping && (
                        <span className="flex items-center text-xs text-blue-600">
                          <Sparkles className="mr-1 h-3 w-3" />
                          {t('chat_typing')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 flex space-x-3 md:ml-4 md:mt-0">
              <button onClick={startNewChat} className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50">
                <Plus className="mr-2 h-4 w-4" />
                {t('chat_new')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-slate-200/70 bg-white/90 shadow">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="mb-4 text-lg font-medium leading-6 text-gray-900">{t('chat_select_pet_optional')}</h3>
                {pets && pets.length > 0 ? (
                  <div className="space-y-3">
                    {pets.map((pet) => (
                      <div
                        key={pet.id}
                        onClick={() => switchPetConversation(pet)}
                        className={`cursor-pointer rounded-lg border-2 p-3 transition-all ${
                          selectedPet?.id === pet.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
                            <Heart className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="ml-3">
                            <h4 className="text-sm font-medium text-gray-900">{pet.name}</h4>
                            <p className="text-xs text-gray-500">{formatSpecies(pet.species)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-4 text-center">
                    <Heart className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                    <p className="text-sm text-gray-500">{t('chat_no_pets')}</p>
                  </div>
                )}
                {selectedPet?.id && (
                  <div className="mt-4 space-y-2">
                    <button onClick={() => runPetPrivacyAction('clear_memory', t('chat_pet_memory_cleared'))} className="w-full rounded bg-gray-100 px-2 py-2 text-xs hover:bg-gray-200">
                      {t('chat_clear_pet_memory')}
                    </button>
                    <button onClick={() => runPetPrivacyAction('clear_chat_history', t('chat_pet_history_deleted'))} className="w-full rounded bg-red-100 px-2 py-2 text-xs text-red-700 hover:bg-red-200">
                      {t('chat_delete_pet_chat_history')}
                    </button>
                    <button onClick={() => runPetPrivacyAction('clear_medical_documents', t('chat_pet_docs_deleted'))} className="w-full rounded bg-amber-100 px-2 py-2 text-xs text-amber-800 hover:bg-amber-200">
                      {t('chat_delete_pet_documents')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-9">
            <div className="rounded-2xl border border-slate-200/70 bg-white/90 shadow">
              <div className="px-4 py-5 sm:p-6">
                {error && (
                  <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4">
                    <div className="flex">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                      <div className="ml-3">
                        <p className="text-sm text-red-800">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                {messages.length === 0 && (
                  <div className="mb-5 rounded-[24px] border border-slate-200/70 bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.14),_transparent_42%),linear-gradient(135deg,rgba(248,250,252,0.98),rgba(239,246,255,0.9))] p-5 shadow-sm">
                    <div className="mb-3 flex items-start gap-2 rounded-2xl border border-blue-200 bg-blue-50/80 px-3 py-2 text-sm text-blue-900">
                      <Stethoscope className="mt-0.5 h-4 w-4 flex-shrink-0" />
                      <span>{t('chat_upload_notice')}</span>
                    </div>
                    <h4 className="mb-3 text-sm font-medium text-slate-700">{t('chat_quick_actions')}</h4>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {quickActions.map((action) => (
                        <button
                          key={action.text}
                          onClick={() => handleQuickAction(action.text)}
                          className="flex items-center space-x-3 rounded-2xl border border-white/80 bg-white/80 p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:bg-white"
                        >
                          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
                            <action.icon className="h-4 w-4" />
                          </span>
                          <span className="text-sm text-slate-700">{action.text}</span>
                          <Zap className="ml-auto h-4 w-4 text-blue-500" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mb-4 h-[34rem] overflow-y-auto rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,rgba(248,250,252,0.95),rgba(255,255,255,0.98))] p-4 shadow-inner">
                  {messages.length === 0 ? (
                    <div className="flex h-full flex-col items-center justify-center text-center text-slate-500">
                      <div className="mb-4 rounded-full bg-blue-100 p-4">
                        <Brain className="h-10 w-10 text-blue-600" />
                      </div>
                      <h3 className="mb-2 text-lg font-medium text-slate-900">{t('chat_start_conversation')}</h3>
                    </div>
                  ) : (
                    <div className="space-y-5">
                      {messages.map((message) => (
                        <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`flex max-w-[88%] items-start gap-3 lg:max-w-[72%] ${message.type === 'user' ? 'flex-row-reverse' : ''}`}>
                            <div className="mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-2xl bg-blue-100 text-blue-700 shadow-sm">
                              {message.type === 'ai' ? <Bot className="h-4 w-4" /> : <User className="h-4 w-4" />}
                            </div>
                            <div
                              className={`rounded-[24px] px-4 py-3 shadow-sm ring-1 ${
                                message.type === 'user'
                                  ? 'bg-blue-50 text-slate-900 ring-blue-200'
                                  : 'bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(241,245,249,0.95))] text-slate-900 ring-slate-200 shadow-[0_16px_36px_-24px_rgba(15,23,42,0.45)]'
                              }`}
                            >
                              {message.type === 'ai' && (
                                <div className="mb-3 flex items-center gap-2 border-b border-slate-200 pb-3">
                                  <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-700 ring-1 ring-blue-100">
                                    <Sparkles className="h-3.5 w-3.5" />
                                    AI Vet
                                  </span>
                                </div>
                              )}
                              {message.isImageConsult && (
                                <div className={`mb-3 flex flex-wrap gap-2 border-b pb-3 ${
                                  message.type === 'user' ? 'border-blue-200' : 'border-slate-200'
                                }`}>
                                  <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${
                                    message.type === 'user'
                                      ? 'bg-white text-blue-700 ring-1 ring-blue-200'
                                      : 'bg-blue-50 text-blue-700 ring-1 ring-blue-100'
                                  }`}>
                                    <ImageIcon className="h-3.5 w-3.5" />
                                    {message.imageCount} {message.imageCount > 1 ? 'Images' : 'Image'}
                                  </span>
                                  {(message.imageNames || []).map((name) => (
                                    <span
                                      key={name}
                                      className={`inline-flex max-w-[180px] items-center rounded-full px-3 py-1 text-xs ${
                                        message.type === 'user'
                                          ? 'bg-white text-slate-700 ring-1 ring-blue-100'
                                          : 'bg-white text-slate-700 ring-1 ring-slate-200'
                                      }`}
                                    >
                                      <span className="truncate">{name}</span>
                                    </span>
                                  ))}
                                </div>
                              )}
                              <div className={`${message.type === 'user' ? 'text-slate-800' : 'text-slate-700'}`}>
                                {renderFormattedContent(message.content)}
                              </div>
                              <div className={`mt-3 flex items-center gap-2 text-[11px] ${
                                message.type === 'user' ? 'text-slate-500' : 'text-slate-400'
                              }`}>
                                <span>
                                  {new Date(message.timestamp).toLocaleTimeString('en-HK', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: false,
                                    timeZone: 'Asia/Hong_Kong'
                                  })}
                                </span>
                                {message.intent && (
                                  <span className={`rounded-full px-2.5 py-1 font-medium ${
                                    message.type === 'user'
                                      ? 'bg-white text-blue-700 ring-1 ring-blue-100'
                                      : 'bg-white text-slate-600 ring-1 ring-slate-200'
                                  }`}>
                                    {message.intent}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {isLoading && (
                        <div className="flex justify-start">
                          <div className="flex max-w-[88%] items-start gap-3 lg:max-w-[72%]">
                            <div className="mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-2xl bg-blue-100 text-blue-700 shadow-sm">
                              <Bot className="h-4 w-4" />
                            </div>
                            <div className="rounded-[24px] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(241,245,249,0.95))] px-4 py-3 text-slate-900 shadow-sm ring-1 ring-slate-200">
                              <div className="flex items-center">
                                <Loader2 className="mr-2 h-4 w-4 animate-spin text-blue-600" />
                                <p className="text-sm text-slate-700">{t('chat_thinking')}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    setSelectedImage(file);
                    setSelectedImagePreview(URL.createObjectURL(file));
                  }}
                />
                <input
                  ref={docInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    setSelectedDocument(file);
                  }}
                />

                {selectedImagePreview && (
                  <div className="mb-3 flex items-center gap-3 rounded-2xl border border-blue-200 bg-blue-50/70 p-3">
                    <img src={selectedImagePreview} alt="selected" className="h-14 w-14 rounded object-cover" />
                    <div className="flex-1 text-sm text-slate-700">
                      {selectedImage?.name} ({t('chat_photo_consultation_enabled')})
                    </div>
                    <button
                      onClick={() => {
                        setSelectedImage(null);
                        setSelectedImagePreview('');
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="rounded bg-red-100 px-2 py-1 text-xs text-red-700"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}

                {selectedDocument && (
                  <div className="mb-3 flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-3">
                    <FileText className="h-5 w-5 text-amber-700" />
                    <div className="flex-1 text-sm text-amber-900">{selectedDocument.name}</div>
                    <button onClick={uploadMedicalDocument} disabled={isUploadingDoc} className="rounded border border-amber-300 bg-amber-100 px-2 py-1 text-xs text-amber-800">
                      {isUploadingDoc ? t('chat_uploading') : t('chat_upload_document')}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedDocument(null);
                        if (docInputRef.current) docInputRef.current.value = '';
                      }}
                      className="rounded bg-red-100 px-2 py-1 text-xs text-red-700"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}

                {uploadedDocs.length > 0 && (
                  <div className="mb-3 rounded-2xl border border-green-200 bg-green-50 px-3 py-2">
                    <p className="mb-1 text-xs font-medium text-green-800">{t('chat_recent_docs')}</p>
                    <div className="space-y-1">
                      {uploadedDocs.map((doc) => (
                        <p key={doc.id} className="text-xs text-green-700">- {doc.original_filename}</p>
                      ))}
                    </div>
                  </div>
                )}

                <div className="rounded-[24px] border border-slate-200/80 bg-white/95 p-3 shadow-sm">
                  <div className="flex flex-col gap-3 lg:flex-row">
                    <div className="relative flex-1">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(event) => setNewMessage(event.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={isTyping ? t('chat_typing') : t('chat_input_placeholder')}
                      disabled={isLoading}
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100 disabled:opacity-50"
                    />
                    </div>
                    <button
                      onClick={() => {
                        if (!selectedPet?.id) {
                          setError(t('chat_select_pet_photo'));
                          return;
                        }
                        fileInputRef.current?.click();
                      }}
                      disabled={isLoading}
                      className="inline-flex items-center justify-center rounded-2xl bg-slate-100 px-4 py-3 text-slate-700 transition hover:bg-slate-200 disabled:opacity-50"
                    >
                      <ImageIcon className="mr-2 h-4 w-4" /> {t('chat_upload')}
                    </button>
                    <button
                      onClick={() => {
                        if (!selectedPet?.id) {
                          setError(t('chat_select_pet_document'));
                          return;
                        }
                        docInputRef.current?.click();
                      }}
                      disabled={isLoading || isUploadingDoc}
                      className="inline-flex items-center justify-center rounded-2xl bg-amber-100 px-4 py-3 text-amber-800 transition hover:bg-amber-200 disabled:opacity-50"
                    >
                      <FileText className="mr-2 h-4 w-4" /> {t('chat_medical_doc')}
                    </button>
                    <button
                      onClick={sendMessage}
                      disabled={!newMessage.trim() || isLoading}
                      className="inline-flex min-w-[120px] items-center justify-center rounded-2xl bg-blue-600 px-5 py-3 text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          <span>{t('chat_sending')}</span>
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          <span>{t('chat_send')}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;

